import {
  isRetailCodeScan,
  looksLikeProductScan,
  retailCodeGtin14,
  scanGtinMiddle12,
  toPickVerifyUnit,
  type PickVerifyUnitInput,
  type ProductScanLike
} from '@/lib/scanning/retailCode';
import {
  minutesSince,
  orderUsesPickVerifyShip,
  pickVerifyStepFor,
  salesOrderVerifyPath,
  type OrderFulfillmentWorkflowFields
} from '@/lib/pickVerifyShip';
import { formatDateOnly } from '@/lib/utils/dateUtils';

/**
 * The pure half of the Pick → Verify → Ship (Option 2) Pick screen: the per-line step machine
 * (zone → one unit → grab the rest), what a scan means at each step, how the list is ordered, and
 * how the screen reads the server's answers (claim, stage, refusals). Kept free of React and of the
 * scanner so every rule a picker depends on is pinned by pickLineSteps.test.ts.
 *
 * THE SERVER DECIDES. Nothing here accepts a line: the zone is resolved through `zoneByScanCode`
 * and the unit through `salesOrderPickConfirmLine`. FEFO's zone / expiration are suggestions: any
 * zone of the line's `eligibleZones` is taken, and the server re-checks it (the unit only for its
 * product — never its expiration). What this module adds is only what must happen
 * BEFORE a round trip — ignoring a label still in frame, refusing a second scan while one is in
 * flight, telling a picker who scanned a rack label in the unit step what to scan instead.
 */

// ─────────────────────────────── the list the server returns ───────────────────────────────

export type PickLineKind = 'SERIALIZED' | 'BULK' | 'UPC';
/** `picking`: some of the line's zones are confirmed and more are still ahead on the walk. */
export type PickLineStatus = 'open' | 'picking' | 'picked' | 'short';

/** The Zone fields this screen selects (a superset of what ZoneScanOverlay's ScannedZone needs). */
export type PickZone = {
  id: string;
  name: string;
  zoneType?: string | null;
  pickSequence?: number | null;
  /** The quarantine zone a Missing quarantine goes to must be at the same location. */
  locationId?: string | null;
};

export type PickStopSuggestion = {
  zone: PickZone | null;
  lot?: string | null;
  /** MDate — an ISO instant; read as a UTC calendar date. */
  expiration?: string | null;
  quantity: number;
};

export type PickLineProduct = {
  id?: string | null;
  title?: string | null;
  fdaProduct?: {
    id?: string | null;
    name?: string | null;
    elevenDigitNdc?: string | null;
    deaSchedule?: string | null;
  } | null;
  upcProduct?: { id?: string | null; upc?: string | null } | null;
};

/**
 * GraphQL `SalesOrderPickWalkStop`: one zone to visit for one line. A line whose stock sits in
 * several zones has one per zone, each placed on the walk by its own zone.
 */
export type PickWalkStop = {
  /** `<orderItemId>:<zoneId>`. */
  key: string;
  orderItemId: string;
  /** 0-based position along the walk. */
  rank: number;
  zone: PickZone | null;
  /** Units of the line to take from this zone. */
  quantity: number;
  /** The zone's suggested (shortest-dated) lot and expiration. */
  lot?: string | null;
  expiration?: string | null;
  /** Units of eligible stock in that suggested row. */
  available?: number | null;
  /** The zone's rows the units come from, in order. */
  rows?: PickStopSuggestion[] | null;
  /** 1-based place among the line's zone stops (confirmed ones first), and how many it has. */
  part: number;
  partCount: number;
};

/** GraphQL `SalesOrderPickConfirmedStop`: a zone already confirmed for a line. */
export type PickConfirmedStop = {
  zone: PickZone | null;
  /** Units taken there (what was found, when `short`). */
  quantity: number;
  lot?: string | null;
  expiration?: string | null;
  /** The picker found fewer here than the zone's share. */
  short?: boolean | null;
  /** What to grab there: the zone's share down its rows, the scanned lot first. */
  rows?: PickStopSuggestion[] | null;
  confirmedAt?: string | null;
  confirmedByName?: string | null;
  /** Unit-scan (controlled) lines: units scanned here — `quantity` is the zone's share. */
  scannedQuantity?: number | null;
  /** Unit-scan (controlled) lines: the units scanned here, in scan order. */
  units?: PickScannedUnit[] | null;
};

/** GraphQL `SalesOrderPickScannedUnit`: one unit of a controlled line scanned at Pick. */
export type PickScannedUnit = {
  serialNumber: string;
  gtin?: string | null;
  lot?: string | null;
  expiration?: string | null;
  zone?: PickZone | null;
  manualEntry?: boolean | null;
  scannedAt?: string | null;
  scannedByName?: string | null;
};

/** GraphQL `SalesOrderPickLine`. */
export type PickLine = {
  orderItemId: string;
  rank: number;
  product?: PickLineProduct | null;
  productName: string;
  ndc?: string | null;
  upc?: string | null;
  deaSchedule?: string | null;
  kind: string;
  quantity: number;
  isShortDated?: boolean | null;
  isChargeback?: boolean | null;
  /** A DEA controlled substance (CI–CV). */
  controlled?: boolean | null;
  /**
   * Controlled, and this user lacks "Fulfill Controlled Sales Order": shown in its place on the walk,
   * but not selectable — the server refuses every write on it. Missing from an older server: false.
   */
  controlledLocked?: boolean | null;
  /**
   * Every unit is scanned (controlled lines): the quantity is the units scanned — never typed — and
   * the line is picked only once they reach `quantity`. Missing from an older server: false.
   */
  unitScanRequired?: boolean | null;
  /** Units scanned on the line (unit-scan lines). */
  scannedQuantity?: number | null;
  /** The units scanned on the line, zone by zone (unit-scan lines). */
  scannedUnits?: PickScannedUnit[] | null;
  /** The suggested (FEFO) zone. */
  expectedZone?: PickZone | null;
  expectedLot?: string | null;
  expectedExpiration?: string | null;
  expectedAvailable?: number | null;
  additionalStops?: PickStopSuggestion[] | null;
  /** Every stop the line is grabbed from, in order (see pickGrabStops). Missing from an older server. */
  grabStops?: PickStopSuggestion[] | null;
  noStock?: boolean | null;
  /**
   * Every zone holding eligible stock of the line outside the skipped ones (FEFO order, the
   * suggestion first) — the zones confirm accepts. Missing from an older server: the suggestion only.
   */
  eligibleZones?: PickZone[] | null;
  /** Zones the picker found empty of this product; the server's allocation leaves them out. */
  skippedZones?: PickZone[] | null;
  /** Stock of this product quarantined at Pick on this order. Missing from an older server. */
  quarantines?: PickQuarantine[] | null;
  status: string;
  foundQuantity?: number | null;
  /** Short lines: the reason recorded (always one for a unit-scan line). */
  shortReason?: string | null;
  /** Units taken at the confirmed zones (a short line: its found quantity; 0 when open). */
  pickedQuantity?: number | null;
  /** Every zone confirmed for the line, in confirmation order. */
  confirmedStops?: PickConfirmedStop[] | null;
  /** The first confirmed zone. */
  confirmedZone?: PickZone | null;
  confirmedAt?: string | null;
  confirmedByName?: string | null;
};

export type PickQuarantineReason = 'missing' | 'damaged';

/** GraphQL `SalesOrderPickQuarantine`: stock quarantined at Pick (Missing / Damaged). */
export type PickQuarantine = {
  reason: string;
  /** Missing: the zone found empty. Damaged: the stock zone the unit(s) stay in, quarantined. */
  zone?: PickZone | null;
  /** Missing only: where the stock went. */
  quarantineZone?: PickZone | null;
  quantity: number;
  serialNumber?: string | null;
  lot?: string | null;
  expiration?: string | null;
  quarantinedAt?: string | null;
};

export type PickListWorkflow = OrderFulfillmentWorkflowFields & {
  pickerClaimedAt?: string | null;
  pickerLastActiveAt?: string | null;
};

/** GraphQL `SalesOrderPickList`. */
export type PickList = {
  salesOrderId: string;
  vendorSalesOrderNumber?: string | null;
  vendorStatus: string;
  clientName?: string | null;
  workflow?: PickListWorkflow | null;
  claimedByMe: boolean;
  pathConfigured: boolean;
  anchorZone?: PickZone | null;
  anchorSource?: string | null;
  anchorOnPath?: boolean | null;
  direction?: string | null;
  /** THE WALK: every zone stop still to visit, of every line, in walk order. Missing from an older server. */
  stops?: PickWalkStop[] | null;
  /** Lines in walk order (by their next stop); finished and unpickable lines after. */
  lines: PickLine[];
  pickedLineCount: number;
  shortLineCount: number;
  openLineCount: number;
  /** Lines locked for this user (`controlledLocked`). Missing from an older server. */
  controlledLockedLineCount?: number | null;
  canComplete: boolean;
  completeBlockedReason?: string | null;
  usesTotes: boolean;
  toteName?: string | null;
};

/**
 * The answer every Pick action gives the component that asked. `handled` means the page already
 * did something about a refusal (left for the list, went on to Verify, re-read a lost claim) and
 * the caller must not alert on top of it; otherwise `message` is the operator-facing reason. `list`
 * is the server's answer, for a caller that has to read the line it just changed before React
 * re-renders with it.
 */
export type PickActionResult =
  | { ok: true; list?: PickList | null }
  /** `reason`: the refusal's `extensions.reason`, when it carried one (e.g. UPC_NOT_RECOGNIZED). */
  | { ok: false; handled: boolean; message: string; reason?: string | null };

/** What a Pick quarantine answered: what it did, and (as every Pick action) the new list. */
export type PickQuarantineResult =
  | { ok: true; list?: PickList | null; quarantine: PickQuarantine | null }
  | { ok: false; handled: boolean; message: string };

export type PickQuarantineMissingArgs = {
  orderItemId: string;
  zoneId: string;
  quarantineZoneId: string;
};

export type PickQuarantineDamagedArgs = {
  orderItemId: string;
  zoneId: string;
  unit: PickVerifyUnitInput;
  /** BULK / UPC only (default 1 on the server). */
  quantity?: number | null;
};

export type PickConfirmLineArgs = {
  orderItemId: string;
  zoneId: string;
  unit: PickVerifyUnitInput;
};

/** One more unit of a unit-scan (controlled) line, at a zone already confirmed for it. */
export type PickScanUnitArgs = {
  orderItemId: string;
  zoneId?: string | null;
  unit: PickVerifyUnitInput;
};

// ─────────────────── server copy this screen reacts to (keep in step with libs/services) ───────────────────

/** `PICK_VERIFY_NOT_PICKING_MESSAGE` (libs/services pick-verify/workflow.ts). */
export const PICK_VERIFY_NOT_PICKING_MESSAGE =
  'This order is no longer in picking (cancelled, on hold or split). Return any picked goods to stock.';
/** `PICK_VERIFY_NOT_THIS_ORDER_MESSAGE` (libs/services pick-verify/workflow.ts). */
export const PICK_VERIFY_NOT_THIS_ORDER_MESSAGE =
  'This order follows the standard fulfillment workflow — open it from the standard Pick screen.';

// Matched on the opening words, not the whole sentence: the first 50 characters of every Option-2
// refusal carry its instruction (old handhelds truncate there), so they are the stable part.
const NOT_PICKING_PREFIX = 'This order is no longer in picking';
const NOT_THIS_ORDER_PREFIX = 'This order follows the standard fulfillment workflow';
/** `PICK_ALREADY_COMPLETE_MESSAGE` (pick.service.ts). */
const PICK_COMPLETE_PREFIX = 'Pick is complete for this order';
/** `PICK_NOT_STARTED_MESSAGE` — the claim this screen held is gone (released or cleared). */
const PICK_NOT_STARTED_PREFIX = 'Start Pick on this order first';
/** `pickClaimRefusal` — someone else holds (or held, now idle) the claim. */
const CLAIM_REFUSAL_PATTERN =
  /is picking this order \(last active|pick of this order has been idle/;
/** `NotFoundError('Sales order')` → "Sales order not found". */
const ORDER_NOT_FOUND_PREFIX = 'Sales order not found';

export type PickRefusalKind =
  | 'lost'
  | 'notThisOrder'
  | 'pickComplete'
  | 'claim'
  | 'other';

/**
 * What an operator-facing refusal means for the SCREEN, beyond the message itself:
 *  - `lost`: the order left picking (cancelled, held, split) or no longer exists — stop scanning
 *    and go back to the list; there is nothing left to pick here.
 *  - `notThisOrder`: it resolves to the standard workflow now — the standard screens own it.
 *  - `pickComplete`: Pick finished (another tab, a retry that landed) — Verify is next.
 *  - `claim`: this handheld no longer holds the one-picker claim — re-read the list so the
 *    "being picked by" panel (or a fresh auto-start) replaces a screen whose writes would fail.
 *  - `other`: a refusal about THIS scan or line (wrong product, no pickable stock, …) — show it
 *    and let the picker try again.
 */
export const classifyPickRefusal = (
  message: string | null | undefined
): PickRefusalKind => {
  const text = typeof message === 'string' ? message.trim() : '';
  if (!text) return 'other';
  if (text.startsWith(NOT_PICKING_PREFIX) || text.startsWith(ORDER_NOT_FOUND_PREFIX))
    return 'lost';
  if (text.startsWith(NOT_THIS_ORDER_PREFIX)) return 'notThisOrder';
  if (text.startsWith(PICK_COMPLETE_PREFIX)) return 'pickComplete';
  if (text.startsWith(PICK_NOT_STARTED_PREFIX) || CLAIM_REFUSAL_PATTERN.test(text))
    return 'claim';
  return 'other';
};

// ─────────────────────────────── where the screen belongs ───────────────────────────────

export type PickListGuard =
  | { kind: 'stay' }
  | { kind: 'verify'; path: string }
  | { kind: 'leave'; title: string; message: string };

/**
 * Whether this order still belongs on the Pick screen, read from the list the server just returned
 * (the load, and every mutation's answer). The same routing rule as everywhere else in the app
 * (`pickVerifyStepFor`): Option 2 at `picking`, stage pick → stay; stage verify (or a verified order
 * waiting to ship) → Verify; anything else → back to the list with the reason.
 */
export const pickListGuard = (list: PickList): PickListGuard => {
  const order = {
    id: list.salesOrderId,
    vendorStatus: list.vendorStatus,
    fulfillmentWorkflow: list.workflow
  };
  const step = pickVerifyStepFor(order);
  if (step === 'pick') return { kind: 'stay' };
  if (step === 'verify' || step === 'documents') {
    return { kind: 'verify', path: salesOrderVerifyPath(list.salesOrderId) };
  }
  if (!orderUsesPickVerifyShip(order)) {
    return {
      kind: 'leave',
      title: 'Standard workflow',
      message: PICK_VERIFY_NOT_THIS_ORDER_MESSAGE
    };
  }
  return {
    kind: 'leave',
    title: 'Not in picking',
    message: PICK_VERIFY_NOT_PICKING_MESSAGE
  };
};

// ─────────────────────────────── the one-picker claim ───────────────────────────────

export type PickClaimView =
  | { kind: 'mine' }
  | { kind: 'free' }
  | { kind: 'other'; name: string; minutesIdle: number; stale: boolean };

/**
 * Who may pick this order right now, from this handheld's point of view. `free` covers an order Pick
 * has never started (it is stamped by the first start) and one whose claim was released: both are
 * claimed automatically on open. `other` is a claim held by someone else — blocking, and taken over
 * only once the server says it is stale (never on the client's clock alone).
 */
export const pickClaimView = (
  list: PickList,
  now: Date | number = new Date()
): PickClaimView => {
  if (list.claimedByMe) return { kind: 'mine' };
  const workflow = list.workflow || {};
  if (!workflow.pickerUserId) return { kind: 'free' };
  const name = typeof workflow.pickerName === 'string' ? workflow.pickerName.trim() : '';
  return {
    kind: 'other',
    name: name || 'Another picker',
    minutesIdle: minutesSince(
      workflow.pickerLastActiveAt ?? workflow.pickerClaimedAt,
      now
    ),
    stale: workflow.pickerClaimStale === true
  };
};

/** Open the order for this picker without a tap: a free claim is taken, an own claim refreshed. */
export const pickShouldAutoStart = (view: PickClaimView): boolean =>
  view.kind === 'free' || view.kind === 'mine';

// ─────────────────────────────── list rows ───────────────────────────────

export const isPickLineDone = (line: Pick<PickLine, 'status'>): boolean =>
  line.status === 'picked' || line.status === 'short';

/**
 * Rows in the server's walk order with finished lines sunk to the bottom, so the next line to pick
 * is always the first one. Stable: the server's order (rank, then position) decides within each group.
 */
export const orderPickRows = <T extends Pick<PickLine, 'status' | 'rank'>>(
  lines: readonly T[]
): T[] =>
  lines
    .map((line, index) => ({ line, index }))
    .sort((a, b) => {
      const aDone = isPickLineDone(a.line);
      const bDone = isPickLineDone(b.line);
      if (aDone !== bDone) return aDone ? 1 : -1;
      const byRank =
        (a.line.rank ?? Number.MAX_SAFE_INTEGER) -
        (b.line.rank ?? Number.MAX_SAFE_INTEGER);
      return byRank || a.index - b.index;
    })
    .map(({ line }) => line);

/**
 * One row of the Pick list. A line still to pick has one row per ZONE STOP — a product in zones 1
 * and 4 is two rows, each where the walk reaches its zone — so `stop` is the zone this row sends the
 * picker to. `stop` is null for a finished line and for one with nothing left to visit (no stock,
 * or every zone skipped): those are one row each.
 */
export type PickRow = {
  key: string;
  line: PickLine;
  stop: PickWalkStop | null;
  /** The line's OTHER stops still to visit, in walk order (the "+ 2 at B-01, stop 7" hints). */
  otherStops: PickWalkStop[];
  /**
   * The line's first row on the list: the one that carries the line-wide actions (mark short, the
   * confirmed zones and their Undo), so a line in three zones does not offer them three times.
   */
  first: boolean;
};

/**
 * The list's rows, in walk order: every zone stop of the server's walk (`list.stops`), then the
 * lines still to pick that have no stop to visit, then the finished lines. From an older server
 * without `stops`: one row per line, as `orderPickRows` orders them.
 */
export const pickWalkRows = (list: Pick<PickList, 'lines' | 'stops'>): PickRow[] => {
  const lines = list.lines || [];
  if (!Array.isArray(list.stops)) {
    return orderPickRows(lines).map((line) => ({
      key: line.orderItemId,
      line,
      stop: null,
      otherStops: [],
      first: true
    }));
  }
  const lineById = new Map(lines.map((line) => [line.orderItemId, line]));
  const stops = [...list.stops]
    .map((stop, index) => ({ stop, index }))
    .sort((a, b) => a.stop.rank - b.stop.rank || a.index - b.index)
    .map(({ stop }) => stop)
    .filter((stop) => {
      const line = lineById.get(stop.orderItemId);
      return !!line && !isPickLineDone(line);
    });
  const stopsByLine = new Map<string, PickWalkStop[]>();
  for (const stop of stops) {
    stopsByLine.set(stop.orderItemId, [
      ...(stopsByLine.get(stop.orderItemId) || []),
      stop
    ]);
  }
  const seen = new Set<string>();
  const rows: PickRow[] = stops.map((stop) => {
    const first = !seen.has(stop.orderItemId);
    seen.add(stop.orderItemId);
    return {
      key: stop.key,
      line: lineById.get(stop.orderItemId)!,
      stop,
      otherStops: (stopsByLine.get(stop.orderItemId) || []).filter(
        (other) => other !== stop
      ),
      first
    };
  });
  const rest = orderPickRows(lines.filter((line) => !stopsByLine.has(line.orderItemId)));
  return [
    ...rows,
    ...rest.map((line) => ({
      key: line.orderItemId,
      line,
      stop: null,
      otherStops: [],
      first: true
    }))
  ];
};

/**
 * The line as the sheet picks it at one stop: that stop's zone, lot and expiration are the
 * suggestion (the line's own `expected*` name only its NEXT stop, which may be a different zone).
 * Every other field is the line's — `eligibleZones` is still what a zone scan may be accepted at.
 */
export const pickLineAtStop = (
  line: PickLine,
  stop: PickWalkStop | null | undefined
): PickLine =>
  stop?.zone
    ? {
        ...line,
        expectedZone: stop.zone,
        expectedLot: stop.lot ?? null,
        expectedExpiration: stop.expiration ?? null,
        expectedAvailable: stop.available ?? null
      }
    : line;

/** Units still to pick: the line quantity less what its confirmed zones already gave. */
export const pickRemainingQuantity = (line: PickLine): number =>
  Math.max(0, line.quantity - (line.pickedQuantity ?? 0));

/**
 * "Stop 3" for a stop on the pick walk (a rank along an unsequenced list is not a stop anyone can
 * walk to), or null.
 */
export const pickStopNumber = (
  stop: Pick<PickWalkStop, 'rank' | 'zone'> | null | undefined
): number | null =>
  stop && typeof stop.zone?.pickSequence === 'number' ? stop.rank + 1 : null;

/** "Zone 1 of 2" when the line is picked across several zones, else null. */
export const pickStopPartLabel = (
  stop: Pick<PickWalkStop, 'part' | 'partCount'> | null | undefined
): string | null =>
  stop && stop.partCount > 1 ? `Zone ${stop.part} of ${stop.partCount}` : null;

/** "+ 2 at B-01 (stop 7)" for each of the line's other stops still ahead. */
export const pickOtherStopHints = (otherStops: readonly PickWalkStop[]): string[] =>
  otherStops
    .filter((stop) => stop.quantity > 0)
    .map((stop) => {
      const number = pickStopNumber(stop);
      return `+ ${stop.quantity} at ${stop.zone?.name || 'another zone'}${
        number !== null ? ` (stop ${number})` : ''
      }`;
    });

/** "B-01 (stop 3)" for every stop the list has for the line, in walk order. */
export const pickLineStopsSummary = (
  orderItemId: string,
  stops: readonly PickWalkStop[] | null | undefined
): string[] =>
  [...(stops || [])]
    .filter((stop) => stop.orderItemId === orderItemId)
    .sort((a, b) => a.rank - b.rank)
    .map((stop) => {
      const number = pickStopNumber(stop);
      return `${stop.zone?.name || 'another zone'}${
        number !== null ? ` (stop ${number})` : ''
      }`;
    });

/**
 * The WarehouseProductDisplay shape for a line: the line's own Product when it resolved (its FDA /
 * UPC relations as they are — a UPC product has no FDA product, and inventing one from the line
 * would change its schedule pill), otherwise — a product removed from the catalog after ordering —
 * one built from the name and codes the server put on the line, so the row still says what to pick.
 */
export const pickLineProduct = (line: PickLine): PickLineProduct => {
  const product = line.product || null;
  if (product) return { ...product, title: product.title?.trim() || line.productName };
  return {
    title: line.productName,
    fdaProduct:
      line.ndc || line.deaSchedule
        ? { elevenDigitNdc: line.ndc ?? null, deaSchedule: line.deaSchedule ?? null }
        : null,
    upcProduct: line.upc ? { upc: line.upc } : null
  };
};

export type PickLineStop =
  | { kind: 'none' }
  | {
      kind: 'zone';
      stopNumber: number | null;
      zoneName: string;
      /** Units to take at this zone (the line quantity for a one-zone line). */
      quantity: number;
      /** "Zone 1 of 2", or null for a one-zone line. */
      partLabel: string | null;
    };

/**
 * The stop badge: "Stop N" only for a zone on the pick walk (a rank along an unsequenced list is not
 * a stop anyone can walk to — the same rule as the standard picking screen), then the zone. With a
 * walk stop, that stop's zone and share; without (an older server), the line's suggested zone.
 */
export const pickLineStop = (
  line: PickLine,
  stop?: PickWalkStop | null
): PickLineStop => {
  if (stop?.zone) {
    return {
      kind: 'zone',
      stopNumber: pickStopNumber(stop),
      zoneName: stop.zone.name,
      quantity: stop.quantity,
      partLabel: pickStopPartLabel(stop)
    };
  }
  const zone = line.expectedZone;
  if (!zone || line.noStock) return { kind: 'none' };
  return {
    kind: 'zone',
    stopNumber: typeof zone.pickSequence === 'number' ? line.rank + 1 : null,
    zoneName: zone.name,
    quantity: pickRemainingQuantity(line),
    partLabel: null
  };
};

/** "+ 4 more from B-02" for every further FEFO stop the line's quantity needs. */
export const additionalStopHints = (line: PickLine): string[] =>
  (line.additionalStops || [])
    .filter((stop) => stop.quantity > 0)
    .map((stop) => `+ ${stop.quantity} more from ${stop.zone?.name || 'another zone'}`);

/** "2027-03-31" — an MDate as the UTC calendar date the label printed, or null. */
export const pickDate = (value: string | null | undefined): string | null => {
  const formatted = formatDateOnly(value);
  return formatted || null;
};

/**
 * What the row shows under a SERIALIZED product: the suggested (shortest-dated) expiration and,
 * smaller, the lot — guidance only, as Pick checks no unit's expiration. Null for BULK / UPC lines.
 */
export const pickLineExpectation = (
  line: PickLine
): { expiration: string | null; lot: string | null } | null => {
  if (line.kind !== 'SERIALIZED') return null;
  const expiration = pickDate(line.expectedExpiration);
  const lot = line.expectedLot?.trim() || null;
  if (!expiration && !lot) return null;
  return { expiration, lot };
};

/**
 * "Picked ✓" / "Short · 2 of 5 found" / "3 of 5 picked" / null for an open line. A unit-scan line
 * counts scans: "3 of 5 scanned", "Short · 2 of 5 scanned".
 */
export const pickLineStatusLabel = (line: PickLine): string | null => {
  if (line.status === 'picked') return 'Picked ✓';
  if (pickLineScansUnits(line)) {
    if (line.status === 'short') return `Short · ${pickScanProgressLabel(line)}`;
    if (line.status === 'picking') return pickScanProgressLabel(line);
    return null;
  }
  if (line.status === 'short')
    return `Short · ${line.foundQuantity ?? 0} of ${line.quantity} found`;
  if (line.status === 'picking')
    return `${line.pickedQuantity ?? 0} of ${line.quantity} picked`;
  return null;
};

/**
 * The found quantity "mark short" records from the list row: 0 for a line with nothing picked, and
 * for a line part-way through its zones the units its confirmed zones already gave (those are in
 * the tote — the server refuses fewer), i.e. "mark the REST short".
 */
export const pickRowShortFound = (line: PickLine): number =>
  line.status === 'picking' ? line.pickedQuantity ?? 0 : 0;

/** A line still to finish: neither picked nor already short. Only such a line can be closed. */
export const pickLineNeedsClose = (line: Pick<PickLine, 'status'>): boolean =>
  line.status !== 'picked' && line.status !== 'short';

/**
 * The "Close line" button offered once a quarantine left the line no pickable stock. It marks the
 * line short exactly as the row's button does (pickRowShortFound): none found, or — part-picked —
 * the rest. The unfound quantity is cancelled when Pick is completed.
 */
export const pickCloseLineLabel = (line: Pick<PickLine, 'status'>): string =>
  line.status === 'picking' ? 'Close line (mark the rest short)' : 'Close line (mark short)';

// ─────────────────────────────── controlled lines: every unit scanned ───────────────────────────────
//
// A controlled line (DEA CI–CV) is picked UNIT BY UNIT: after the zone and its first unit, the picker
// scans each unit's 2D barcode until the scans reach the quantity ordered. The quantity is never
// typed; a short is the units scanned plus a reason. The server enforces all of it — these helpers
// only let the device answer instantly (duplicates, over-scans, a linear barcode) and say where
// things stand.

/** A unit-scan (controlled) line: every unit is scanned, the quantity is never typed. */
export const pickLineScansUnits = (
  line: Pick<PickLine, 'unitScanRequired'> | null | undefined
): boolean => !!line?.unitScanRequired;

/** Units scanned on a unit-scan line. */
export const pickScannedCount = (
  line: Pick<PickLine, 'scannedQuantity' | 'scannedUnits'>
): number => line.scannedQuantity ?? line.scannedUnits?.length ?? 0;

/** "3 of 5 scanned". */
export const pickScanProgressLabel = (
  line: Pick<PickLine, 'scannedQuantity' | 'scannedUnits' | 'quantity'>
): string => `${pickScannedCount(line)} of ${line.quantity} scanned`;

/** A confirmed zone of a unit-scan line still short of its share (the server's default scan zone). */
export const pickUnitScanOpenStop = (
  line: Pick<PickLine, 'confirmedStops'>
): PickConfirmedStop | null =>
  (line.confirmedStops || []).find(
    (stop) => !!stop.zone && (stop.scannedQuantity ?? 0) < stop.quantity
  ) ?? null;

/**
 * Where a part-scanned unit-scan line resumes when its row is tapped: the confirmed zone still short
 * of its share — or, when no other zone holds the rest, the last confirmed zone (the shelf may hold
 * more than its records say). Null when the line is not part-way, or its rest is a stop of its own.
 */
export const pickUnitScanResumeZone = (line: PickLine): PickZone | null => {
  if (!pickLineScansUnits(line) || line.status !== 'picking') return null;
  const open = pickUnitScanOpenStop(line);
  if (open?.zone) return open.zone;
  if (!line.noStock && line.expectedZone) return null;
  const confirmed = (line.confirmedStops || []).filter((stop) => !!stop.zone);
  return confirmed[confirmed.length - 1]?.zone ?? null;
};

/** The identity of a scanned package: its GTIN middle-12 and serial (case-insensitive). */
export const pickUnitScanKey = (result: ProductScanLike): string | null => {
  const serial = (result?.serialNumber || '').trim().toLowerCase();
  if (!serial) return null;
  return `${scanGtinMiddle12(result) ?? ''}|${serial}`;
};

/** Whether the line already has this serial scanned (the server's duplicate rule, answered locally). */
export const pickLineHasSerial = (
  line: Pick<PickLine, 'scannedUnits'>,
  result: ProductScanLike
): string | null => {
  const serial = (result?.serialNumber || '').trim().toLowerCase();
  if (!serial) return null;
  const hit = (line.scannedUnits || []).find(
    (unit) => unit.serialNumber.trim().toLowerCase() === serial
  );
  return hit ? hit.serialNumber : null;
};

/** How long a camera re-read of the unit just counted is ignored (it is usually still in frame). */
export const PICK_UNIT_SCAN_REREAD_WINDOW_MS = 2500;

/** The reasons offered for a short of a controlled line; "Other" takes free text. */
export const PICK_SHORT_REASONS = [
  'Not enough on the shelf',
  'Damaged units',
  'Expired or short-dated units',
  'Units in quarantine'
] as const;

/** `PICK_VERIFY_SHORT_REASON_MAX_LENGTH` (libs/db order.model.ts). */
export const PICK_SHORT_REASON_MAX_LENGTH = 500;

/** A short reason the form may submit (trimmed, 1–500 characters), else null. */
export const parseShortReason = (value: string | null | undefined): string | null => {
  const text = (value ?? '').trim();
  return text && text.length <= PICK_SHORT_REASON_MAX_LENGTH ? text : null;
};

/** What recording a unit-scan line short means, said before the picker confirms it. */
export const pickUnitScanShortText = (
  line: Pick<PickLine, 'productName' | 'quantity' | 'scannedQuantity' | 'scannedUnits'>
): string => {
  const scanned = pickScannedCount(line);
  return `${line.productName}: ${scanned} of ${line.quantity} scanned. The ${scanned} scanned ${
    scanned === 1 ? 'unit is' : 'units are'
  } what is picked; the other ${line.quantity - scanned} ${
    line.quantity - scanned === 1 ? 'is' : 'are'
  } recorded short and flagged for review.`;
};

/** "Over-scan: all 5 of X are already scanned." — the server's `PICK_CONTROLLED_OVER_SCAN_MESSAGE`. */
export const pickOverScanMessage = (line: Pick<PickLine, 'quantity' | 'productName'>): string =>
  `Over-scan: all ${line.quantity} of ${line.productName} are already scanned.`;

/** The server's `PICK_CONTROLLED_DUPLICATE_SERIAL_MESSAGE`. */
export const pickDuplicateSerialMessage = (serialNumber: string): string =>
  `Duplicate: serial ${serialNumber} is already scanned on this order.`;

/** The server's `PICK_CONTROLLED_SERIAL_REQUIRED_MESSAGE`. */
export const pickSerialRequiredMessage = (productName: string): string =>
  `Scan the unit's 2D barcode: ${productName} is controlled, so every unit's serial number is recorded.`;

// ─────────────────────────────── skipping an empty zone ───────────────────────────────

/** The names of the zones the picker found empty of this line's product, in the server's order. */
export const pickSkippedZoneNames = (line: Pick<PickLine, 'skippedZones'>): string[] =>
  (line.skippedZones || [])
    .map((zone) => zone?.name)
    .filter((name): name is string => !!name);

/** "Skipped as empty: A-01, B-02" under an open row, or null when nothing was skipped. */
export const pickSkippedZonesLabel = (
  line: Pick<PickLine, 'skippedZones'>
): string | null => {
  const names = pickSkippedZoneNames(line);
  return names.length ? `Skipped as empty: ${names.join(', ')}` : null;
};

/**
 * Why a line cannot be opened: nothing is recorded in stock at all, or every zone holding it was
 * skipped as empty. Either way the line can only be marked short — or, after skips, a skip undone.
 */
export const pickNoStockCopy = (
  line: Pick<PickLine, 'productName' | 'skippedZones' | 'status' | 'pickedQuantity'>
): { title: string; text: string } => {
  const skipped = pickSkippedZoneNames(line).length > 0;
  if (line.status === 'picking') {
    // Part of the line is in the tote; it is the REST that no other zone has.
    return {
      title: 'No other zone has the rest',
      text: `${line.pickedQuantity ?? 0} of ${
        line.productName
      } are picked. No other zone has the rest — mark the rest short${
        skipped ? ', or undo a skipped zone' : ''
      }.`
    };
  }
  return skipped
    ? {
        title: 'No other zone has it',
        text: `No other zone has ${line.productName} — mark the line short, or undo a skipped zone.`
      }
    : {
        title: 'No stock recorded',
        text: `No pickable stock of ${line.productName} is recorded — mark the line short.`
      };
};

// ─────────────────────────────── controlled lines ───────────────────────────────

/** Whether this user may not act on the line: controlled (CI–CV) without the permission. */
export const isPickLineLocked = (line: Pick<PickLine, 'controlledLocked'>): boolean =>
  line.controlledLocked === true;

/** The DEA schedule a locked line shows ("CII"), else a generic word. */
const controlledLabel = (line: Pick<PickLine, 'deaSchedule'>): string =>
  (typeof line.deaSchedule === 'string' && line.deaSchedule.trim()) || 'Controlled';

/** The badge on a locked row: what it is and why it cannot be tapped. */
export const pickLockedBadge = (line: Pick<PickLine, 'deaSchedule'>): string =>
  `${controlledLabel(line)} · controlled picker only`;

/**
 * Why a locked line does not open — the same rule the server refuses it with
 * (`PICK_CONTROLLED_LINE_LOCKED_MESSAGE`, libs/services pick-verify/controlled.ts).
 */
export const pickLockedLineCopy = (
  line: Pick<PickLine, 'productName' | 'deaSchedule'>
): { title: string; text: string } => ({
  title: 'Controlled product',
  text: `${line.productName} is a controlled substance (${controlledLabel(
    line
  )}). Only a picker with the "Fulfill Controlled Sales Order" permission can pick it — pick the other lines, then release the order.`
});

/**
 * The note above the list when lines are locked for this user (null when none are): they keep their
 * place on the walk; somebody with the permission picks them once this picker has released the order.
 */
export const pickLockedLinesNote = (
  list: Pick<PickList, 'controlledLockedLineCount' | 'lines'>
): string | null => {
  const count =
    typeof list.controlledLockedLineCount === 'number'
      ? list.controlledLockedLineCount
      : (list.lines || []).filter(isPickLineLocked).length;
  if (count < 1) return null;
  const lines = count === 1 ? '1 controlled line is' : `${count} controlled lines are`;
  return `${lines} locked — you need the "Fulfill Controlled Sales Order" permission to pick ${
    count === 1 ? 'it' : 'them'
  }. Pick the other lines, then Release so a controlled picker can finish the order.`;
};

/**
 * What the picker is told once a zone was skipped, read from the line (and the walk) in the
 * server's answer: the zone's share is now a stop of its own wherever the walk reaches its new zone
 * — named, so the picker carries on along the walk rather than crossing the floor for it — or
 * nothing is left but a short.
 */
export const pickSkipOutcome = (
  skippedZoneName: string,
  line: PickLine | null | undefined,
  stops?: readonly PickWalkStop[] | null
):
  | { kind: 'next'; title: string; text: string }
  | { kind: 'noStock'; title: string; text: string } => {
  if (!line || line.noStock || !line.expectedZone) {
    return {
      kind: 'noStock',
      title: `${skippedZoneName} skipped`,
      text: line
        ? pickNoStockCopy(line).text
        : 'No other zone has this product — mark the line short.'
    };
  }
  const where = Array.isArray(stops) ? pickLineStopsSummary(line.orderItemId, stops) : [];
  return {
    kind: 'next',
    title: `${skippedZoneName} skipped`,
    text: where.length
      ? `${line.productName} is now picked at ${where.join(
          ', then '
        )} — it is on the list where the walk gets there.`
      : `Go to ${line.expectedZone.name} and scan its label.`
  };
};

// ─────────────────────────────── the grab (step 3) ───────────────────────────────

export type PickGrabStop = {
  /** Null only for a further stop whose zone the server could not resolve. */
  zoneId: string | null;
  zoneName: string;
  quantity: number;
  expiration: string | null;
  lot: string | null;
};

const grabStopOf = (stop: PickStopSuggestion): PickGrabStop => ({
  zoneId: stop.zone?.id ?? null,
  zoneName: stop.zone?.name || 'another zone',
  quantity: stop.quantity,
  expiration: pickDate(stop.expiration),
  lot: stop.lot?.trim() || null
});

/**
 * Every place this line's units come from, in order: the server's `grabStops` — the suggestion's
 * FEFO stops, or, for a line confirmed away from the suggestion, the row the picker confirmed first
 * and then FEFO (the order Verify draws bulk units in). From an older server without them: the
 * expected zone first (its share is the smaller of the line quantity and what that bucket holds —
 * `expectedAvailable` is the bucket's stock, not the allocation), then each additional stop.
 */
export const pickGrabStops = (line: PickLine): PickGrabStop[] => {
  if (!line.expectedZone) return [];
  const grab = (line.grabStops || []).filter((stop) => stop.quantity > 0);
  if (grab.length) return grab.map(grabStopOf);
  const available =
    typeof line.expectedAvailable === 'number' && line.expectedAvailable > 0
      ? line.expectedAvailable
      : line.quantity;
  const first: PickGrabStop = {
    zoneId: line.expectedZone.id,
    zoneName: line.expectedZone.name,
    quantity: Math.min(line.quantity, available),
    expiration: pickDate(line.expectedExpiration),
    lot: line.expectedLot?.trim() || null
  };
  const rest = (line.additionalStops || [])
    .filter((stop) => stop.quantity > 0)
    .map(grabStopOf);
  return [first, ...rest];
};

/** The confirmed zone a grab is for: the one at `zoneId`, else the latest confirmed. */
export const pickConfirmedStopAt = (
  line: PickLine,
  zoneId: string | null | undefined
): PickConfirmedStop | null => {
  const confirmed = line.confirmedStops || [];
  return (
    (zoneId ? confirmed.find((stop) => stop.zone?.id === zoneId) : null) ??
    confirmed[confirmed.length - 1] ??
    null
  );
};

/**
 * What to grab at the zone just confirmed: THAT zone's share, down its rows (the scanned lot
 * first) — the line's other zones are stops of their own further along the walk. From an older
 * server without `confirmedStops`: every stop of the line (pickGrabStops).
 */
export const pickZoneGrabStops = (
  line: PickLine,
  zoneId: string | null | undefined
): PickGrabStop[] => {
  const confirmed = pickConfirmedStopAt(line, zoneId);
  if (!confirmed) return pickGrabStops(line);
  const rows = (confirmed.rows || []).filter((row) => row.quantity > 0).map(grabStopOf);
  if (rows.length) return rows;
  return [
    {
      zoneId: confirmed.zone?.id ?? null,
      zoneName: confirmed.zone?.name || 'this zone',
      quantity: confirmed.quantity,
      expiration: pickDate(confirmed.expiration),
      lot: confirmed.lot?.trim() || null
    }
  ];
};

/** Units of the line taken at the zone just confirmed (the line quantity from an older server). */
export const pickZoneShare = (
  line: PickLine,
  zoneId: string | null | undefined
): number => {
  const confirmed = pickConfirmedStopAt(line, zoneId);
  return confirmed ? confirmed.quantity : line.quantity;
};

/** The per-zone "Couldn't find them all here" prompt. */
export const pickZoneShortPrompt = (zoneName: string, share: number): string =>
  `How many of the ${share} did you find in ${zoneName}?`;

/**
 * After a zone, what is left of the line: the rest is picked at its other zones when the walk gets
 * there (named, with their stop numbers), or no other zone has it and the rest can only be marked
 * short. Null when the line is finished.
 */
export const pickNextStopNote = (
  line: PickLine,
  stops: readonly PickWalkStop[] | null | undefined
): { kind: 'later' | 'noStock'; text: string } | null => {
  if (line.status !== 'picking') return null;
  if (line.noStock || !line.expectedZone)
    return { kind: 'noStock', text: pickNoStockCopy(line).text };
  const remaining = pickRemainingQuantity(line);
  const where = pickLineStopsSummary(line.orderItemId, stops);
  return {
    kind: 'later',
    text: `${remaining} more ${remaining === 1 ? 'unit' : 'units'} at ${
      where.length ? where.join(', then ') : line.expectedZone.name
    } — pick ${remaining === 1 ? 'it' : 'them'} there when the walk gets to ${
      where.length > 1 ? 'each zone' : 'that zone'
    }.`
  };
};

/**
 * What a per-zone short did, read from the line in the server's answer: the rest moved to the
 * line's other zones (still `picking`), or no other zone had it and the line is now short.
 */
export const pickZoneShortOutcome = (
  zoneName: string,
  line: PickLine | null | undefined,
  stops: readonly PickWalkStop[] | null | undefined
): { title: string; text: string } => {
  if (line?.status === 'short') {
    return {
      title: 'Line marked short',
      text: `No other zone has the rest of ${line.productName} — marked short with ${
        line.foundQuantity ?? 0
      } of ${line.quantity} found.`
    };
  }
  const note = line ? pickNextStopNote(line, stops) : null;
  return {
    title: `Recorded what ${zoneName} had`,
    text: note?.text ?? 'The list shows where to pick the rest.'
  };
};

/** Units of the line that no recorded stock covers (the picker will have to mark them short). */
export const pickGrabShortfall = (line: PickLine): number => {
  const allocated = pickGrabStops(line).reduce((sum, stop) => sum + stop.quantity, 0);
  return Math.max(0, line.quantity - allocated);
};

const unitsWord = (quantity: number) => (quantity === 1 ? 'unit' : 'units');

/**
 * The step-3 instruction, naming EVERY stop: "Grab 3 from A-12 (exp 2027-01-31), then 2 from B-02
 * (exp 2027-03-31)." A single-stop line reads "Grab 5 units from A-12".
 */
export const pickGrabInstruction = (line: PickLine): string => {
  const stops = pickGrabStops(line);
  if (!stops.length) return `Grab ${line.quantity} ${unitsWord(line.quantity)}.`;
  if (stops.length === 1) {
    const [only] = stops;
    return `Grab ${only.quantity} ${unitsWord(only.quantity)} from ${only.zoneName}.`;
  }
  const parts = stops.map(
    (stop) =>
      `${stop.quantity} from ${stop.zoneName}${
        stop.expiration ? ` (exp ${stop.expiration})` : ''
      }`
  );
  return `Grab ${parts[0]}, then ${parts.slice(1).join(', then ')}.`;
};

/**
 * The "Couldn't find them all" prompt. With further stops it sends the picker there first — the
 * units the first zone lacked are meant to come from them, and a short recorded before looking
 * would split off stock the warehouse actually has.
 */
export const pickShortPrompt = (line: PickLine): string => {
  const others = pickGrabStops(line).slice(1);
  const lookFirst = others.length
    ? `Check ${others
        .map((stop) => `${stop.zoneName} (${stop.quantity})`)
        .join(' and ')} too. `
    : '';
  return `${lookFirst}How many of the ${line.quantity} did you find in total?`;
};

/**
 * A typed found quantity for a short: a whole number from `min` (0) to quantity − 1 (finding all of
 * them is not a short — that is Done). Null for anything else, so the form can refuse it before the server.
 */
export const parseFoundQuantity = (
  value: string | number | null | undefined,
  quantity: number,
  /** The fewest that may be recorded: 1 at a zone where a unit was just scanned. */
  min = 0
): number | null => {
  const text = typeof value === 'number' ? String(value) : (value ?? '').trim();
  if (!/^\d+$/.test(text)) return null;
  const found = Number(text);
  if (!Number.isSafeInteger(found) || found < min || found > quantity - 1) return null;
  return found;
};

// ─────────────────────────────── quarantine at Pick ───────────────────────────────

/** "Quarantined: 5 missing from A-01 → QZ-1 · 1 damaged in A-02" under the row, or null. */
export const pickQuarantinedLabel = (
  line: Pick<PickLine, 'quarantines'>
): string | null => {
  const parts = (line.quarantines || [])
    .filter((quarantine) => quarantine && quarantine.quantity > 0)
    .map((quarantine) => {
      const zoneName = quarantine.zone?.name || 'a zone';
      if (quarantine.reason === 'missing') {
        const to = quarantine.quarantineZone?.name;
        return `${quarantine.quantity} missing from ${zoneName}${to ? ` → ${to}` : ''}`;
      }
      return `${quarantine.quantity} damaged in ${zoneName}`;
    });
  return parts.length ? `Quarantined: ${parts.join(' · ')}` : null;
};

/**
 * What the picker is told once a quarantine went through, read from the line (and the walk's stops)
 * in the server's answer. Damaged: the stock stays where it is, quarantined — pick a good unit
 * (`stay`), unless that was the last of it (`noStock`). Missing: the zone no longer holds the
 * product, so — exactly like a skip, whose wording it reuses — where the line is picked next, or that
 * nothing is left but a short; the sheet then closes.
 */
export const pickQuarantineOutcome = (
  quarantine: PickQuarantine | null | undefined,
  line: PickLine | null | undefined,
  stops?: readonly PickWalkStop[] | null
): { kind: 'stay' | 'next' | 'noStock'; title: string; text: string } => {
  const zoneName = quarantine?.zone?.name || 'the zone';
  if (quarantine?.reason === 'damaged') {
    const what = quarantine.serialNumber
      ? `Serial ${quarantine.serialNumber}`
      : `${quarantine.quantity} ${unitsWord(quarantine.quantity)}${
          quarantine.lot ? ` of lot ${quarantine.lot}` : ''
        }`;
    // No good unit left anywhere: there is nothing to pick instead, only a short.
    if (line && pickLineNeedsClose(line) && (line.noStock || !line.expectedZone)) {
      return {
        kind: 'noStock',
        title: 'Damaged stock quarantined',
        text: `${what} quarantined in ${zoneName}. ${pickNoStockCopy(line).text}`
      };
    }
    return {
      kind: 'stay',
      title: 'Damaged stock quarantined',
      text: `${what} quarantined in ${zoneName}. Set it aside and pick a good unit.`
    };
  }
  const quantity = quarantine?.quantity ?? 0;
  const moved =
    quantity > 0
      ? `${quantity} ${unitsWord(quantity)} moved from ${zoneName} to ${
          quarantine?.quarantineZone?.name || 'quarantine'
        }.`
      : `Nothing was left to quarantine in ${zoneName}.`;
  const next = pickSkipOutcome(zoneName, line, stops);
  return { kind: next.kind, title: `${zoneName} quarantined`, text: `${moved} ${next.text}` };
};

/** The damaged unit as the panel shows it: "Serial X · Lot L · Exp 2027-01-31", or the code. */
export const pickDamagedUnitLabel = (unit: PickVerifyUnitInput | null | undefined): string => {
  if (!unit) return '';
  const parts = [
    unit.serialNumber ? `Serial ${unit.serialNumber}` : null,
    unit.lot ? `Lot ${unit.lot}` : null,
    unit.expiration ? `Exp ${unit.expiration}` : null
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : unit.code || unit.gtin || '';
};

/**
 * A typed damaged quantity (BULK / UPC): a whole number of at least 1. Null otherwise, so the
 * panel can refuse it before the server does.
 */
export const parseDamagedQuantity = (value: string | number | null | undefined): number | null => {
  const text = typeof value === 'number' ? String(value) : (value ?? '').trim();
  if (!/^\d+$/.test(text)) return null;
  const quantity = Number(text);
  return Number.isSafeInteger(quantity) && quantity >= 1 ? quantity : null;
};

export type PickQuarantineZoneOutcome =
  | { kind: 'accepted'; zone: PickZone }
  /** A zone, but a stock zone — Missing stock goes to a quarantine zone. */
  | { kind: 'notQuarantine'; zone: PickZone | null }
  | { kind: 'notFound' }
  | { kind: 'lookupFailed'; error: unknown };

/**
 * Is the scanned label a quarantine zone? The lookup is scoped to the emptied zone's location, so
 * any quarantine-type match is one the server will take (it re-checks the location regardless).
 */
export const classifyQuarantineZoneLookup = (
  lookup: PickZoneLookup
): PickQuarantineZoneOutcome => {
  if (lookup.error) return { kind: 'lookupFailed', error: lookup.error };
  const resolved = [lookup.zone, ...(lookup.matches || [])].filter(
    (zone): zone is PickZone => !!zone?.id
  );
  if (!resolved.length) return { kind: 'notFound' };
  const quarantine = resolved.find((zone) => zone.zoneType === 'quarantine');
  if (quarantine) return { kind: 'accepted', zone: quarantine };
  return { kind: 'notQuarantine', zone: lookup.zone };
};

// ─────────────────────────────── the line sheet's step machine ───────────────────────────────

/**
 * `zone` → `unit` (one unit) → `grab` (the zone's share, unscanned). A unit-scan (controlled) line
 * goes `unit` → `scan` instead: every further unit is scanned there, never grabbed or typed.
 */
export type PickSheetStep = 'zone' | 'unit' | 'grab' | 'scan';

export type PickSheetState = {
  orderItemId: string;
  step: PickSheetStep;
  /** The zone the scanned / typed label resolved to — the suggestion or another eligible zone. */
  acceptedZone: PickZone | null;
  /**
   * The exact text that was accepted as the zone. The rack label stays in frame when the camera
   * re-arms for the unit step (and a QR label decodes under the item profiles too), so a re-read of
   * it is not a product scan to complain about.
   */
  acceptedZoneCode: string | null;
  /**
   * The lookup / confirm in flight, or null. One at a time (ZoneScanOverlay's `settledRef`, as
   * state): a second trigger pull or a camera re-read while the server is answering is dropped, and
   * an answer arriving for a request this sheet no longer waits on is ignored.
   */
  pendingRequestId: number | null;
  /** The page-owned manual entry panel (SharedScanner's own modal cannot be closed by the page). */
  manualOpen: boolean;
  /** Step 3's "Couldn't find them all" found-quantity entry. */
  shortEntryOpen: boolean;
  /** The Quarantine panel (step 2 — once the zone label was scanned), or null. */
  quarantine: PickQuarantineState | null;
  /**
   * Step `scan`: the unit last counted (its key and when), so a camera re-read of the pack still in
   * frame is ignored instead of refused as a duplicate.
   */
  lastScan?: { key: string; at: number } | null;
};

/** `choose` asks Missing or Damaged; then the chosen flow. */
export type PickQuarantineMode = 'choose' | PickQuarantineReason;

export type PickQuarantineState = {
  mode: PickQuarantineMode;
  /** The zone it is about: step 2's accepted zone — the one whose label the picker scanned. */
  zone: PickZone;
  /** Missing: the quarantine zone the stock goes to (scanned, or chosen from the list). */
  quarantineZone: PickZone | null;
  /** Damaged: the unit scanned (or typed) — quarantined only on the explicit tap. */
  unit: PickVerifyUnitInput | null;
  /**
   * Missing: the panel's zone section is not waiting for a label — a zone is chosen (changing it
   * takes a tap on "Change"), or the list is open. The camera then reads nothing for the panel.
   * Unset means waiting, which is how the panel opens.
   */
  zoneScanPaused?: boolean;
};

export type PickSheetAction =
  | { type: 'open'; orderItemId: string }
  | { type: 'close' }
  | { type: 'zoneLookupStarted'; requestId: number }
  | { type: 'zoneAccepted'; requestId: number; zone: PickZone; code: string }
  | { type: 'zoneRejected'; requestId: number }
  | { type: 'unitStarted'; requestId: number }
  /**
   * The zone's first unit was accepted. `unitScan` (a controlled line) goes on to scan every unit;
   * `scanned` is that unit, so its re-read is not a duplicate.
   */
  | {
      type: 'unitAccepted';
      requestId: number;
      unitScan?: boolean;
      scanned?: { key: string; at: number } | null;
    }
  | { type: 'unitRejected'; requestId: number }
  | { type: 'shortStarted'; requestId: number }
  | { type: 'shortSettled'; requestId: number }
  /** Step `scan`: one more unit (or a removal) in flight, and its answer. */
  | { type: 'scanStarted'; requestId: number }
  | { type: 'scanSettled'; requestId: number; scanned?: { key: string; at: number } | null }
  /** "Zone is empty — skip it": any step, one request at a time like every other. */
  | { type: 'skipStarted'; requestId: number }
  /**
   * The skip answered. `skipped` when it took: a zone accepted on step 2 was the one skipped (or the
   * line now goes elsewhere), so the picker scans the next zone's label from step 1.
   */
  | { type: 'skipSettled'; requestId: number; skipped: boolean }
  | { type: 'openManual' }
  | { type: 'closeManual' }
  | { type: 'openShortEntry' }
  | { type: 'closeShortEntry' }
  /**
   * "Quarantine" (step 2 only): ask Missing or Damaged about `zone`. Not on step 1 — Missing moves
   * every unit recorded in the zone, so the picker must first prove where they are by scanning it.
   */
  | { type: 'openQuarantine'; zone: PickZone }
  | { type: 'chooseQuarantine'; mode: PickQuarantineReason }
  /** Back to the Missing / Damaged choice, dropping what the chosen flow had captured. */
  | { type: 'quarantineBack' }
  | { type: 'closeQuarantine' }
  | { type: 'quarantineZoneChosen'; zone: PickZone | null }
  /** Missing: the panel's zone section started or stopped waiting for a label. */
  | { type: 'quarantineZoneScanning'; scanning: boolean }
  | { type: 'damagedUnitCaptured'; unit: PickVerifyUnitInput }
  /** A quarantine-zone label lookup (Missing): one request at a time, like every other. */
  | { type: 'quarantineLookupStarted'; requestId: number }
  | { type: 'quarantineLookupSettled'; requestId: number }
  | { type: 'quarantineStarted'; requestId: number }
  /**
   * The quarantine answered. `done` when it took: the panel closes, and a Missing zone accepted on
   * step 2 was just emptied, so — like a skip — the picker scans the next zone's label from step 1.
   */
  | { type: 'quarantineSettled'; requestId: number; done: boolean }
  /** The accepted zone no longer holds eligible stock of the line (a fresher list): scan another. */
  | { type: 'rezone' }
  /**
   * A fresher list has this open line as PICKED while the sheet still asks for its zone / unit — a
   * confirm committed but its answer was lost. The unit is recorded: grab the rest.
   */
  | { type: 'pickedOnServer'; unitScan?: boolean };

/**
 * A fresh sheet at the zone step — or, `scanAt` (a unit-scan line resumed at a zone already
 * confirmed for it), straight at the scan step there.
 */
export const openPickSheet = (orderItemId: string, scanAt: PickZone | null = null): PickSheetState => ({
  orderItemId,
  step: scanAt ? 'scan' : 'zone',
  acceptedZone: scanAt,
  acceptedZoneCode: null,
  pendingRequestId: null,
  manualOpen: false,
  shortEntryOpen: false,
  quarantine: null,
  lastScan: null
});

const startIn = (
  state: PickSheetState,
  step: PickSheetStep,
  requestId: number
): PickSheetState =>
  state.step === step && state.pendingRequestId === null
    ? { ...state, pendingRequestId: requestId }
    : state;

const settles = (state: PickSheetState, requestId: number) =>
  state.pendingRequestId !== null && state.pendingRequestId === requestId;

export const pickSheetReducer = (
  state: PickSheetState | null,
  action: PickSheetAction
): PickSheetState | null => {
  if (action.type === 'open') return openPickSheet(action.orderItemId);
  if (!state) return state;
  switch (action.type) {
    case 'close':
      return null;
    case 'zoneLookupStarted':
      return startIn(state, 'zone', action.requestId);
    case 'zoneAccepted':
      if (!settles(state, action.requestId) || state.step !== 'zone') return state;
      return {
        ...state,
        step: 'unit',
        acceptedZone: action.zone,
        acceptedZoneCode: action.code.trim() || null,
        pendingRequestId: null,
        manualOpen: false
      };
    case 'zoneRejected':
    case 'unitRejected':
    case 'shortSettled':
      return settles(state, action.requestId)
        ? { ...state, pendingRequestId: null }
        : state;
    case 'unitStarted':
      return startIn(state, 'unit', action.requestId);
    case 'unitAccepted':
      if (!settles(state, action.requestId) || state.step !== 'unit') return state;
      return {
        ...state,
        step: action.unitScan ? 'scan' : 'grab',
        pendingRequestId: null,
        manualOpen: false,
        lastScan: action.unitScan ? action.scanned ?? null : null
      };
    case 'shortStarted':
      return startIn(state, state.step === 'scan' ? 'scan' : 'grab', action.requestId);
    case 'scanStarted':
      return startIn(state, 'scan', action.requestId);
    case 'scanSettled':
      if (!settles(state, action.requestId)) return state;
      return {
        ...state,
        pendingRequestId: null,
        ...(action.scanned ? { lastScan: action.scanned } : {})
      };
    case 'skipStarted':
      return state.pendingRequestId === null
        ? { ...state, pendingRequestId: action.requestId }
        : state;
    case 'skipSettled':
      if (!settles(state, action.requestId)) return state;
      if (action.skipped && state.step === 'unit') {
        return {
          ...state,
          step: 'zone',
          acceptedZone: null,
          acceptedZoneCode: null,
          pendingRequestId: null,
          manualOpen: false
        };
      }
      return { ...state, pendingRequestId: null, manualOpen: false };
    case 'openManual':
      return state.step === 'grab' || state.step === 'scan' || state.quarantine
        ? state
        : { ...state, manualOpen: true };
    case 'closeManual':
      return { ...state, manualOpen: false };
    case 'openShortEntry':
      return state.step === 'grab' || state.step === 'scan'
        ? { ...state, shortEntryOpen: true }
        : state;
    case 'closeShortEntry':
      return { ...state, shortEntryOpen: false };
    case 'rezone':
      if (state.step !== 'unit' || state.pendingRequestId !== null) return state;
      return { ...state, step: 'zone', acceptedZone: null, acceptedZoneCode: null };
    case 'pickedOnServer':
      // Not while a lookup / confirm is in flight: its own answer settles the step.
      if (state.step === 'grab' || state.step === 'scan' || state.pendingRequestId !== null)
        return state;
      return {
        ...state,
        step: action.unitScan ? 'scan' : 'grab',
        manualOpen: false,
        shortEntryOpen: false,
        quarantine: null
      };
    case 'openQuarantine':
      if (state.step !== 'unit' || state.pendingRequestId !== null) return state;
      return {
        ...state,
        manualOpen: false,
        quarantine: { mode: 'choose', zone: action.zone, quarantineZone: null, unit: null }
      };
    case 'chooseQuarantine':
      if (!state.quarantine || state.quarantine.mode !== 'choose') return state;
      return { ...state, quarantine: { ...state.quarantine, mode: action.mode } };
    case 'quarantineBack':
      if (!state.quarantine || state.pendingRequestId !== null) return state;
      return {
        ...state,
        quarantine: {
          ...state.quarantine,
          mode: 'choose',
          quarantineZone: null,
          unit: null,
          zoneScanPaused: undefined
        }
      };
    case 'closeQuarantine':
      if (state.pendingRequestId !== null) return state;
      return { ...state, quarantine: null };
    case 'quarantineZoneChosen':
      if (state.quarantine?.mode !== 'missing') return state;
      return { ...state, quarantine: { ...state.quarantine, quarantineZone: action.zone } };
    case 'quarantineZoneScanning': {
      if (state.quarantine?.mode !== 'missing') return state;
      const paused = !action.scanning;
      if (!!state.quarantine.zoneScanPaused === paused) return state;
      return { ...state, quarantine: { ...state.quarantine, zoneScanPaused: paused } };
    }
    case 'damagedUnitCaptured':
      if (state.quarantine?.mode !== 'damaged') return state;
      return { ...state, quarantine: { ...state.quarantine, unit: action.unit } };
    case 'quarantineLookupStarted':
    case 'quarantineStarted':
      return state.quarantine && state.pendingRequestId === null
        ? { ...state, pendingRequestId: action.requestId }
        : state;
    case 'quarantineLookupSettled':
      return settles(state, action.requestId) ? { ...state, pendingRequestId: null } : state;
    case 'quarantineSettled': {
      if (!settles(state, action.requestId)) return state;
      if (!action.done) return { ...state, pendingRequestId: null };
      const emptied = state.quarantine?.mode === 'missing' && state.step === 'unit';
      return {
        ...state,
        ...(emptied ? { step: 'zone' as const, acceptedZone: null, acceptedZoneCode: null } : {}),
        pendingRequestId: null,
        quarantine: null
      };
    }
    default:
      return state;
  }
};

/** Which decoder profile each step needs (the SCAN_PROFILE values, which this module cannot import). */
export type PickScanProfileKey = 'zone' | 'item' | 'product';

/**
 * Step 1 reads rack labels (ZONE). Step 2 reads the unit: a serialized line's 2D DataMatrix (ITEM —
 * the Option-1 item decoder, which never hands back the linear NDC-UPC printed beside it), a
 * BULK / UPC line's retail barcode (PRODUCT). Step 3 keeps step 2's profile: nothing is read there,
 * and switching would re-arm the camera on the unit still in frame for no reason.
 */
export const pickScanProfileKey = (
  step: PickSheetStep,
  kind: string,
  quarantineMode: PickQuarantineMode | null = null
): PickScanProfileKey => {
  // Quarantine → Missing reads the quarantine zone's label; → Damaged reads the damaged unit.
  if (quarantineMode === 'missing') return 'zone';
  if (quarantineMode === 'damaged') return kind === 'SERIALIZED' ? 'item' : 'product';
  if (step === 'zone') return 'zone';
  return kind === 'SERIALIZED' ? 'item' : 'product';
};

export type PickScanDecision =
  | {
      kind: 'ignore';
      reason:
        | 'noSheet'
        | 'grab'
        | 'busy'
        | 'manualOpen'
        | 'empty'
        | 'sameZoneLabel'
        | 'quarantineChoosing'
        | 'quarantineZoneScanPaused'
        | 'reread';
    }
  /** Zone step: a GS1 / SSCC barcode is a product, never a rack label — say so. */
  | { kind: 'productInZoneStep' }
  /**
   * Zone step: resolve the text as a zone. `productLike` marks a retail-code-shaped payload: it is
   * still looked up (a numeric rack label can carry a valid check digit), but if it is not a zone
   * of the line the picker is told it looks like a product barcode rather than "no zone matches".
   */
  | { kind: 'lookupZone'; code: string; productLike: boolean }
  /** Unit step: not a product barcode at all — a soft hint, no server call, no fail tone. */
  | { kind: 'notProduct' }
  /**
   * Quarantine → Damaged, serialized line: the linear retail code, which carries no serial to
   * quarantine. (The unit step takes it: Pick accepts a serialized unit by zone and product alone.)
   */
  | { kind: 'scan2D' }
  | { kind: 'confirmUnit'; unit: PickVerifyUnitInput }
  /** Quarantine → Missing: a product barcode where the quarantine zone's label is wanted. */
  | { kind: 'productInQuarantineZoneStep' }
  /** Quarantine → Missing: resolve the text as the quarantine zone. */
  | { kind: 'lookupQuarantineZone'; code: string }
  /** Quarantine → Damaged: the damaged unit (shown, quarantined on the explicit tap). */
  | { kind: 'damagedUnit'; unit: PickVerifyUnitInput }
  /** Unit-scan line, step `scan`: one more unit to count. */
  | { kind: 'scanUnit'; unit: PickVerifyUnitInput; key: string | null }
  /** Unit-scan line: a barcode without a serial (linear NDC / UPC) — the 2D DataMatrix is required. */
  | { kind: 'needs2D' }
  /** Unit-scan line, step `scan`: this serial is already scanned on the line. */
  | { kind: 'duplicateSerial'; serialNumber: string }
  /** Unit-scan line, step `scan`: every unit ordered is already scanned. */
  | { kind: 'overScan' };

/** What a camera / handheld scan means right now. Manual entry does not come through here. */
export const classifyPickScan = (
  state: PickSheetState | null,
  line:
    | (Pick<PickLine, 'kind'> &
        Partial<Pick<PickLine, 'unitScanRequired' | 'scannedUnits' | 'scannedQuantity' | 'quantity'>>)
    | null,
  result: ProductScanLike,
  now: number = Date.now()
): PickScanDecision => {
  if (!state || !line) return { kind: 'ignore', reason: 'noSheet' };
  // Step 3: the unit was accepted and is probably still in frame; nothing is scanned here.
  if (state.step === 'grab') return { kind: 'ignore', reason: 'grab' };
  if (state.step === 'scan') return classifyUnitScan(state, line, result, now);
  if (state.pendingRequestId !== null) return { kind: 'ignore', reason: 'busy' };
  // The operator is typing — a camera read of whatever is in frame must not act behind the form.
  if (state.manualOpen) return { kind: 'ignore', reason: 'manualOpen' };
  const text = (result?.text || '').trim();

  if (state.quarantine) {
    if (state.quarantine.mode === 'choose') return { kind: 'ignore', reason: 'quarantineChoosing' };
    // The zone label just accepted is usually still in frame; it is neither a quarantine zone nor a unit.
    if (text && state.acceptedZoneCode && text === state.acceptedZoneCode) {
      return { kind: 'ignore', reason: 'sameZoneLabel' };
    }
    if (state.quarantine.mode === 'missing') {
      // A zone is chosen, or the list is open: a label in frame must not replace it untapped.
      if (state.quarantine.zoneScanPaused) {
        return { kind: 'ignore', reason: 'quarantineZoneScanPaused' };
      }
      if (result?.gtin || result?.sscc) return { kind: 'productInQuarantineZoneStep' };
      if (!text) return { kind: 'ignore', reason: 'empty' };
      return { kind: 'lookupQuarantineZone', code: text };
    }
    if (!text && !result?.gtin) return { kind: 'ignore', reason: 'empty' };
    if (!looksLikeProductScan(result)) return { kind: 'notProduct' };
    if (line.kind === 'SERIALIZED' && isRetailCodeScan(result)) return { kind: 'scan2D' };
    return { kind: 'damagedUnit', unit: toPickVerifyUnit(result) };
  }

  if (state.step === 'zone') {
    if (result?.gtin || result?.sscc) return { kind: 'productInZoneStep' };
    if (!text) return { kind: 'ignore', reason: 'empty' };
    return {
      kind: 'lookupZone',
      code: text,
      productLike: retailCodeGtin14(text) !== null
    };
  }

  if (!text && !result?.gtin) return { kind: 'ignore', reason: 'empty' };
  if (text && state.acceptedZoneCode && text === state.acceptedZoneCode) {
    return { kind: 'ignore', reason: 'sameZoneLabel' };
  }
  if (!looksLikeProductScan(result)) return { kind: 'notProduct' };
  // A controlled line's first unit is counted too: it must carry its serial.
  if (pickLineScansUnits(line) && !(result?.gtin && result?.serialNumber)) return { kind: 'needs2D' };
  return { kind: 'confirmUnit', unit: toPickVerifyUnit(result) };
};

/**
 * Step `scan` of a unit-scan line: each product scan is one more unit. The pack just counted is
 * usually still in frame, so the same serial read again within PICK_UNIT_SCAN_REREAD_WINDOW_MS is
 * ignored; after that it is a duplicate (refused here, as the server would). A scan past the quantity
 * ordered, or without a serial, is refused on the spot.
 */
const classifyUnitScan = (
  state: PickSheetState,
  line: Pick<PickLine, 'kind'> &
    Partial<Pick<PickLine, 'unitScanRequired' | 'scannedUnits' | 'scannedQuantity' | 'quantity'>>,
  result: ProductScanLike,
  now: number
): PickScanDecision => {
  if (state.pendingRequestId !== null) return { kind: 'ignore', reason: 'busy' };
  const text = (result?.text || '').trim();
  if (!text && !result?.gtin) return { kind: 'ignore', reason: 'empty' };
  if (text && state.acceptedZoneCode && text === state.acceptedZoneCode) {
    return { kind: 'ignore', reason: 'sameZoneLabel' };
  }
  if (!looksLikeProductScan(result)) return { kind: 'notProduct' };
  if (!(result?.gtin && result?.serialNumber)) return { kind: 'needs2D' };
  const key = pickUnitScanKey(result);
  if (
    key &&
    state.lastScan?.key === key &&
    now - state.lastScan.at < PICK_UNIT_SCAN_REREAD_WINDOW_MS
  ) {
    return { kind: 'ignore', reason: 'reread' };
  }
  const duplicate = pickLineHasSerial({ scannedUnits: line.scannedUnits ?? [] }, result);
  if (duplicate) return { kind: 'duplicateSerial', serialNumber: duplicate };
  const scanned = line.scannedQuantity ?? line.scannedUnits?.length ?? 0;
  if (typeof line.quantity === 'number' && scanned >= line.quantity) return { kind: 'overScan' };
  return { kind: 'scanUnit', unit: toPickVerifyUnit(result), key };
};

/** What `useZoneScanLookup` answers. */
export type PickZoneLookup = {
  zone: PickZone | null;
  matches: PickZone[];
  error?: unknown;
};

export type PickZoneOutcome =
  /** `suggested`: the FEFO zone; otherwise another zone holding eligible stock of the line. */
  | { kind: 'accepted'; zone: PickZone; suggested: boolean }
  /** A zone already picked for this line (an earlier stop of the walk): its share is in the tote. */
  | { kind: 'alreadyPicked'; scannedZone: PickZone }
  /** A zone, but one with no eligible stock of the line. */
  | { kind: 'wrongZone'; scannedZone: PickZone | null }
  | { kind: 'notFound' }
  | { kind: 'lookupFailed'; error: unknown }
  | { kind: 'noExpectedZone' };

/**
 * Is the resolved label a zone this line can be picked from — the suggestion or any other of its
 * `eligibleZones`? Accepted when it is the single match OR one of several: zone names are not
 * unique across locations and the lookup runs company-wide, so a label shared by two warehouses
 * comes back ambiguous — and a zone of the line being among them is exactly what the picker proved
 * by standing there (the suggestion wins a tie, then FEFO order). Anything else is a zone with no
 * eligible stock of the line, or no zone at all. The server re-checks the zone at confirm.
 */
export const classifyZoneLookup = (
  lookup: PickZoneLookup,
  line: Pick<PickLine, 'expectedZone' | 'eligibleZones'> &
    Partial<Pick<PickLine, 'confirmedStops'>>
): PickZoneOutcome => {
  const expectedZone = line.expectedZone;
  if (!expectedZone) return { kind: 'noExpectedZone' };
  if (lookup.error) return { kind: 'lookupFailed', error: lookup.error };
  const resolved = [lookup.zone, ...(lookup.matches || [])].filter(
    (zone): zone is PickZone => !!zone?.id
  );
  if (resolved.some((zone) => zone.id === expectedZone.id)) {
    return { kind: 'accepted', zone: expectedZone, suggested: true };
  }
  const eligible = (line.eligibleZones || []).find((zone) =>
    resolved.some((match) => match.id === zone?.id)
  );
  if (eligible) return { kind: 'accepted', zone: eligible, suggested: false };
  // A zone of the line's that is already picked holds stock — it is just not to be picked again.
  const picked = (line.confirmedStops || [])
    .map((confirmed) => confirmed.zone)
    .find((zone) => !!zone && resolved.some((match) => match.id === zone.id));
  if (picked) return { kind: 'alreadyPicked', scannedZone: picked };
  if (lookup.zone) return { kind: 'wrongZone', scannedZone: lookup.zone };
  if (lookup.matches?.length) return { kind: 'wrongZone', scannedZone: null };
  return { kind: 'notFound' };
};

/**
 * The soft note for a zone accepted that is not the suggestion — informational, never the fail
 * tone: the picker may pick from any zone holding the line. Null for the suggested zone.
 */
export const pickZoneAcceptedNote = (
  outcome: Extract<PickZoneOutcome, { kind: 'accepted' }>,
  expectedName: string
): { title: string; text: string } | null =>
  outcome.suggested
    ? null
    : {
        title: `Not the suggested zone (${expectedName}) — OK`,
        text: `Picking from ${outcome.zone.name}. Scan one unit from this zone.`
      };

/** The alert for a zone that was not accepted (the accepted case has none). */
export const pickZoneRefusalCopy = (
  outcome: Exclude<PickZoneOutcome, { kind: 'accepted' } | { kind: 'lookupFailed' }>,
  {
    code,
    expectedName,
    productLike
  }: { code: string; expectedName: string; productLike: boolean }
): { title: string; text: string } => {
  if (outcome.kind === 'noExpectedZone') {
    return {
      title: 'No stock recorded',
      text: 'No pickable stock of this line is recorded — mark it short.'
    };
  }
  if (outcome.kind === 'alreadyPicked') {
    return {
      title: `Already picked at ${outcome.scannedZone.name}`,
      text: `This line's units from ${outcome.scannedZone.name} are already picked. Its next zone is ${expectedName}.`
    };
  }
  if (productLike) {
    return {
      title: 'That is a product barcode',
      text: `Scan the zone label first — suggested zone: ${expectedName}.`
    };
  }
  if (outcome.kind === 'wrongZone') {
    return {
      title: `No stock of this line here — try ${expectedName}`,
      text: outcome.scannedZone
        ? `${outcome.scannedZone.name} has no pickable stock of this line. Suggested: ${expectedName}.`
        : `“${code}” has no pickable stock of this line. Suggested: ${expectedName}.`
    };
  }
  return {
    title: 'Zone not found',
    text: `No zone matches “${code}”. Scan a rack label — suggested: ${expectedName}.`
  };
};

// ─────────────────────────────── footer ───────────────────────────────

/**
 * Any unit is on its way to the tote: a picked line, one part-way through its zones, or a short one
 * with units found.
 */
export const pickSomethingFound = (lines: readonly PickLine[]): boolean =>
  lines.some(
    (line) =>
      line.status === 'picked' ||
      line.status === 'picking' ||
      (line.status === 'short' && (line.foundQuantity ?? 0) > 0)
  );

/**
 * Add Tote: only with totes on, once goods are going into one, and only until it has one. Totes are
 * required to complete Pick (the server's blocked reason says so) because Option 2 puts the goods in
 * the tote while picking, not at the end.
 */
export const pickShowAddTote = (list: PickList): boolean =>
  !!list.usesTotes && !list.toteName && pickSomethingFound(list.lines || []);

export const pickShortsConfirmText = (shortLineCount: number): string =>
  `${shortLineCount} line(s) short. Picked lines continue to Verify; the rest are split off as a new order and cancelled.`;

/**
 * What Complete Pick answered, for the "Pick complete" panel: the order that goes on to Verify —
 * the original, or after a short split the child holding the picked lines — and the number of the
 * short remainder, which the server cancelled, when there is one.
 */
export type PickCompletion = {
  verifyOrderId: string;
  verifyOrderNumber: string | null;
  shortOrderNumber: string | null;
};

export const pickCompleteText = ({
  verifyOrderNumber,
  shortOrderNumber
}: PickCompletion): string => {
  const ready = verifyOrderNumber
    ? `SO #${verifyOrderNumber} is ready for Verify.`
    : 'The picked lines are ready for Verify.';
  return shortOrderNumber
    ? `${ready} The rest, SO #${shortOrderNumber}, was cancelled.`
    : ready;
};
